import classNames from 'classnames';
import * as _ from 'lodash';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';

import * as api from '../api/api';
import effLogo from '../assets/img/eff/logo.png';
import { FileObject } from '../dff-analyse/dff-analyse';
import { ToolbarAnalyse } from '../toolbar/toolbar-analyse-component';
import { PathUtils } from '../utils';

import './empty-folders-analyse.scss';
import { CheckboxTree } from '../checkbox-tree/checkbox-tree';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Toolbar } from '../toolbar/toolbar-component';
import { Link } from 'react-router-dom';
import { MainMenuComponent } from '../menu/main-menu';

export interface EmptyFoldersAnalyseProps {
  match: any;
  location: any;
}

export const EmptyFoldersAnalyse: React.FC<EmptyFoldersAnalyseProps> = ({
  match,
  location,
}) => {
  const query = queryString.parse(location.search);
  const normalizedQueryPath = query.path[0] !== '/' ? `/${query.path}` : query.path;
  const mountId = match.params.mountId;
  const rootPath = PathUtils.normalize((normalizedQueryPath || '/') as string);

  const [mount, setMount] = useState('');
  const [errorText, setErrorText] = useState('');
  const [isFetchingDone, setIsFetchingDone] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortReverse, setSortReverse] = useState(false);

  const [root, setRoot] = useState<FileObject>({
    file: {
      name: '',
      type: 'dir',
      size: 0,
      modified: 0,
      hash: '',
      tags: {},
      contentType: '',
      isSelected: false,
    },
    path: rootPath,
    splittedPath: [],
    mountId: mountId,
    children: [],
    remaining: 0,
    descendants: 0,
    size: 0,
  });

  const [selectedFolders, setSelectedFolders] = useState<FileObject[]>([]);
  const [emptyFolders, setEmptyFolders] = useState<FileObject[]>([]);

  useEffect(() => {
    setIsFetchingDone(false);

    fetch(`/api/v2/mounts/${mountId}`)
      .then(results => results.json())
      .then(data => {
        setMount(data.name);
      });
  }, [mountId]);

  const buildTree = async (abortSignal: AbortSignal) => {
    const response = await fetch(
      `/content/api/v2/mounts/${mountId}/files/listrecursive?path=${encodeURIComponent(
        rootPath,
      )}`,
      { signal: abortSignal },
    );
    if (response.status !== 200) {
      // TODO display error
      setErrorText(response.statusText);
      return;
    }

    const responseText = await response.text();

    setIsFetchingDone(true);

    const fileLines = responseText.split('\n').filter(line => line !== '');

    const vaultsResponse = await fetch('/api/v2.1/vault/repos');
    const vaultResponseText = await vaultsResponse.text();
    const repos :api.VaultRepos = JSON.parse(vaultResponseText);

    const sharedResponse = await fetch('/api/v2/shared');
    const sharedResponseText = await sharedResponse.text();
    const sharedData :api.Shared = JSON.parse(sharedResponseText);

    const fetchedFiles = fileLines
      .map((res: string) => {
        const file: api.FileResponse = JSON.parse(res);
        if (file.error != null) {
          return undefined;
        }

        //excluding vault folders
        if (repos.repos.find(r => r.mountId === mountId && file.path.startsWith(r.path))) {
          return undefined;
        }

        if (file.file.size === -1) {
          // TODO display this
          file.file.size = 0;
        }

        const filePath = PathUtils.joinPaths(rootPath, file.path);
        const mappedFile: FileObject = {
          file: file.file,
          mountId: mountId,
          path: filePath,
          splittedPath: filePath.split('/').filter(c => c !== ''),
          children: [],
          remaining: file.file.type === 'dir' ? 1 : 0,
          descendants: 0,
          size: file.file.size,
        };
        return mappedFile;
      })
      .filter(file => file !== undefined) as FileObject[];

    //we only need one file in folder to know that we cannot delete it - so we are minimizing our list for building tree
    const fetchedFilesMinimized: FileObject[] = [];
    const paths: String[] = [];

    fetchedFiles.forEach(el => {
      if (el.file.type === 'dir') {
        fetchedFilesMinimized.push(el);
        paths.push(el.path);
      }
      else if (el.file.type === 'file') {
        if (!paths.find(p => p.startsWith(PathUtils.parent(el.path)) && p.length !== PathUtils.parent(el.path).length)) {
          fetchedFilesMinimized.push(el);
          paths.push(el.path);
        }
      }
    });

    fetchedFiles.length = 0;
    paths.length = 0;

    return process(fetchedFilesMinimized, sharedData.files);
  };

  useEffect(() => {
    // abortController is used for aborting this hook on unmount
    const abortController = new AbortController();

    buildTree(abortController.signal);

    return function cleanup() {
      abortController.abort();
    };
  }, [mountId, rootPath]);

  async function process(fetchedFiles: FileObject[], sharedData: api.SharedFile[]) {
    const filesMap = new Map(fetchedFiles.map(file => [file.path, file]));

    let lastParentPath: string | undefined = undefined;
    let lastParentPaths: string[] = [];
    let touchedPaths = new Set<string>();

    // skip the root
    const filesToProcess = fetchedFiles.slice(1);

    let currentRemaining = filesToProcess.length;

    const flush = () => {
      touchedPaths.forEach(path => {
        const file = filesMap.get(path);

        sort(file, ['file.name'], false);
      });

      const root = filesMap.get(rootPath);
      setRoot(root);
      setRemaining(currentRemaining);

      touchedPaths.clear();
    };

    let lastSleep = Date.now();
    let lastFlush = Date.now();

    const refresh = async () => {
      const now = Date.now();

      // yield every 15 ms
      if (now - lastSleep > 15) {
        lastSleep = now;
        await new Promise(resolve => setTimeout(resolve, 1));
      }

      // flush every 200 ms
      if (now - lastFlush > 200) {
        lastFlush = now;
        flush();
      }
    };

    flush();

    for (const file of filesToProcess) {
      currentRemaining--;
      file.isOpen = false;

      // e.g. /foo/bar/baz -> /foo/bar
      const parentPath = PathUtils.parent(file.path);

      // e.g. /prefix/foo/bar -> [/prefix, /prefix/foo, /prefix/foo/bar]
      const parentPaths =
        parentPath === lastParentPath
          ? lastParentPaths
          : PathUtils.chainTo(parentPath, rootPath);

      // if lastParentPaths was [/prefix, /prefix/foo, /prefix/foo/bar, /prefix/foo/bar/baz]
      // and parentPaths is [/prefix, /prefix/next]
      // we need to set [/prefix/foo, /prefix/foo/bar, /prefix/foo/bar/baz] as done
      if (parentPaths.length < lastParentPaths.length) {
        lastParentPaths.slice(parentPaths.length).forEach(path => {
          filesMap.get(path).remaining = 0;
        });
      }
      const parent = filesMap.get(parentPath);
      parent.children.push(file);
      file.parent = parent;

      if (file.file.type === 'file') {
        const size = file.file.size;

        parentPaths.forEach(path => {
          touchedPaths.add(path);

          const file = filesMap.get(path);
          file.descendants += 1;
          file.size += size;
        });
      }

      lastParentPath = parentPath;
      lastParentPaths = parentPaths;

      await refresh();
    }

    const setAncestorsToBeOpened = function(file: FileObject) {
      if (file.parent) {
        file.parent.isOpen = true;
        file.parent.showInTree = true;
        setAncestorsToBeOpened(file.parent);
      }
    }

    const checkIfLocked = function(path: string, sharedData: api.SharedFile[]) {
      if (path === '/My desktop sync')
        return { isLocked: true, tooltip: "This is the default sync folder." };

      if (sharedData.find(s => s.mount.root?.id === mountId && s.mount.root?.path === path))
        return { isLocked: true, tooltip: "This folder is shared." };

      return { isLocked: false };
    }

    filesMap.forEach(file => {
      file.remaining = 0;

      if (file.file.type === 'dir' && file.size === 0 && file.descendants === 0) {
        file.file.isSelected = true;
        file.isOpen = true;
        file.showInTree = true;

        var isLocked = checkIfLocked(file.path, sharedData);

        if (!isLocked.isLocked) {
          setAncestorsToBeOpened(file);
          setEmptyFolders((state) => {
            return [...state, file];
          });
          setSelectedFolders((state) => {
            return [...state, file];
          });
        } else {
          file.isLocked = true;
          file.tooltip = isLocked.tooltip;
        }
      }
    });

    flush();

    setIsDone(true);
  }

  const sortByName = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'name') {
      setSortReverse(true);
      newSort = true;
      setSortColumn('name');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }
    sort(file, ['file.name'], newSort);
    setRoot(file);
  };

  const sortByFiles = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'files') {
      setSortReverse(true);
      newSort = true;

      setSortColumn('files');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }
    sort(file, ['descendants'], newSort);
    setRoot(file);
  };

  const sortBySize = (file: FileObject) => {
    let newSort: boolean;

    if (sortColumn !== 'size') {
      setSortReverse(true);
      newSort = true;

      setSortColumn('size');
    } else {
      newSort = !sortReverse;
      setSortReverse(!sortReverse);
    }

    sort(file, ['size'], newSort);
    setRoot(file);
  };

  const sort = (
    file: FileObject,
    columns: string[],
    sortReverseInit: boolean,
  ) => {
    if (file.children && file.children.length) {
      if (columns.length === 1 && columns[0] === 'file.name') {
        let dirs = file.children.filter(c => c.file.type === 'dir');
        let files = file.children.filter(c => c.file.type !== 'dir');

        dirs = _.sortBy(dirs, columns);
        files = _.sortBy(files, columns);

        if (sortReverseInit) {
          dirs.reverse();
          files.reverse();
        }

        file.children = dirs.concat(files);
      } else {
        file.children = _.sortBy(file.children, columns);

        if (sortReverseInit) {
          file.children.reverse();
        }
      }

      file.children.forEach(child => {
        sort(child, columns, sortReverseInit);
      });
    }
  };

  const toolbarText = () => {
    if (!isFetchingDone)
      return `${mount}${rootPath} | Reading, please wait ...`;

    if (isDeleting)
      return `${mount}${rootPath} | Checking if empty and deleting [${deletedCount}/${selectedFolders.length}], please wait ...`;

    return !isDone
      ? `${mount}${rootPath} | Building tree... Remaining: ${remaining}`
      : `${mount}${rootPath}`;
  };

  const countSelectedFolders = function(root) {
    return selectedFolders.length;
  }

  const countAllFolders = function(root) {
    return emptyFolders.length;
  }

  const handleSelected = (e: React.MouseEvent<HTMLButtonElement>, selectAll: boolean) => {
    e.preventDefault();

    if (selectAll) {
      setSelectedFolders(emptyFolders.map(f => f));
    } else {
      setSelectedFolders([]);
    }

    return true;
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!selectedFolders.length)
      return;

    setIsDeleting(true);
    setDeletedCount(0);

    const leveledMap = new Map<number, String[]>();

    selectedFolders.map(f => { return { path: f.path, level: PathUtils.chain(f.path).length }}).forEach(function(i) {
      if (leveledMap.has(i.level)) {
        const paths = leveledMap.get(i.level);
        paths.push(i.path);
        leveledMap.set(i.level, paths);
      } else {
        leveledMap.set(i.level, [i.path]);
      }
    });

    const deleteApiCall = async (path) => {
      const response = await fetch(`/api/v2.1/mounts/${mountId}/files/remove?path=${encodeURIComponent(path)}&removeIfEmpty`, {
        method: 'DELETE'
      });

      if (response.ok)
        setDeletedCount((cnt) => cnt++);
    };

    const processWithConcurrencyPool = async (items, poolSize = 3) => {
      const results = new Array(items.length);
      let completedCount = 0;
      let nextIndex = 0;

      const pool = new Set();

      const createProcess = async (index) => {
        try {
          const result = await deleteApiCall(items[index]);
          results[index] = result;
          setDeletedCount((cnt) => { return cnt+1 });
        } catch (err) {
          results[index] = { error: err.message };
        }
      };

      const addToPool = () => {
        if (nextIndex < items.length) {
          const promise = createProcess(nextIndex);
          pool.add(promise);

          promise.finally(() => {
            pool.delete(promise);
            addToPool();
          });

          nextIndex++;
        }
      };

      for (let i = 0; i < poolSize && i < items.length; i++) {
        addToPool();
      }

      while (pool.size > 0) {
        await Promise.race(pool);
      }

      return results;
    };

    const levels = Array.from(leveledMap.keys()).sort((a, b) => b - a);

    for(const idx in levels) {
      const level = levels[idx];
      const results = await processWithConcurrencyPool(leveledMap.get(Number(level)), 3);
    }

    setIsDeleting(false);
    setSelectedFolders([]);
    window.location.reload();
  };

  const onEmptyFolderSelect = function(path: string) {
    const file = emptyFolders.find(f => f.path === path);

    //ce je bil selectan parent, ki ima spodaj empty folder, potem tudi child-a obkljukamo
    //ce je bil unselectan parent, ki ima spodaj empty folder, tudi child odkljukamo

    if (selectedFolders.includes(file)) {
      let foldersToUnselect:FileObject[] = emptyFolders.filter(f => f.path.indexOf(file.path) === 0);

      setSelectedFolders((state) => {
        return state.filter(f => !foldersToUnselect.map(m => m.path).includes(f.path));
      });
    } else {
      let foldersToSelect:FileObject[] = emptyFolders.filter(f => f.path.indexOf(file.path) === 0).map(f => f);
      setSelectedFolders((state) => {
        let newState = state.map(f => f);
        foldersToSelect.forEach(f => {
          if (!state.map(sObj => sObj.path).includes(f.path))
            newState.push(f);
        });

        return newState;
      });
    }
  };

  const DeleteOverlay = () => {
    if (isDeleting) {
      return (
        <div>
          <div className="delete-overlay">
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    return null;
  };

  const SelectMenu = () => {
    return (
      <div className="select-menu">
        <span className="select-menu-tag">Select:</span>
        <button className={classNames('select-menu-option')}
          title="Select all empty folders"
          onClick={(e) => handleSelected(e, true)}
          disabled={emptyFolders.length === selectedFolders.length}>Select all</button>
        <button className={classNames('select-menu-option')}
          title="Unselect all empty folders"
          onClick={(e) => handleSelected(e, false)}
          disabled={selectedFolders.length === 0}>Unselect all</button>
      </div>
    );
  };

  const BottomBar = () => {
    return (
      <div className="bottom-bar">
        <div className="bottom-bar__text">
          <p>{`Selected folders: ${countSelectedFolders(root)}/${countAllFolders(root)}`}</p>
        </div>
        <div className="bottom-bar__buttons">
          <button disabled={selectedFolders.length === 0} onClick={handleDelete}>Delete selected</button>
        </div>
      </div>
    );
  };

  if (!isFetchingDone) {
    return (
      <div className="eff-analyse-container">
      <ToolbarAnalyse
        text={toolbarText()}
        logoPath={effLogo}
        newSearchUrl="/eff/"
      />
      <div className="eff-analyse">
        <LoadingSpinner />
      </div>
      </div>
    );
  }

  return (
    <div className="eff-analyse-container">
      <Toolbar>
          <div className='l-toolbar__column'>
            <Link to="/">
              <img src={effLogo} alt="Koofr Empty folder finder logo" className="l-toolbar__logo"/>
            </Link>
            <div className='divider'></div>
            <div className='app-name'>Empty folder finder</div>
          </div>
          <div className='l-toolbar__column stretch'>{toolbarText()}</div>
          <div className='l-toolbar__column'>
            <Link to="/eff" className='new-search'>New Search</Link>
            <div className='divider'></div>
            <MainMenuComponent/>
          </div>
        </Toolbar>
      <div className="eff-analyse">
        {emptyFolders.length ? (
          <div className="menu">
            <SelectMenu />
            <div className="line">&nbsp;</div>
          </div>
        ) : null}

        <div className="main-container">
          <div>
            {errorText ? (
              <h1>{errorText}</h1>
            ) : (
                <div className="main-table">
                  <table className="main-table-head">
                    <thead>
                      <tr>
                        <td
                          className={classNames(
                            'head-column',
                            'col-name',
                            'sort-label',
                            {
                              'sort-by sort-by-rev':
                                sortColumn === 'name' && sortReverse,
                              'sort-by': sortColumn === 'name' && !sortReverse,
                            },
                          )}
                        >
                          <span onClick={() => sortByName(root)}>Name</span>
                        </td>
                      </tr>
                    </thead>
                  </table>

                  <CheckboxTree file={root} onCheckboxChange={onEmptyFolderSelect} selectedPaths={selectedFolders.map(f => f.path)} showOnlyDirs={true} />
                </div>
              )}
          </div>
          {!root.descendants && isDone && root ? (
            <div>
              According to our careful calculations, this folder is empty.
            </div>
          ) : null}
          {root.descendants && emptyFolders.length === 0 && isDone && root ? (
            <div>
              Hooray! According to our careful search, you don't have any empty folders here.
            </div>
          ) : null}
          <BottomBar />
          <DeleteOverlay />
        </div>
      </div>
      {!isDone ? (
        <LoadingSpinner />
      ) : null}
    </div>
  );
};
